/**
 * Helper functions for linked audio/video clips.
 *
 * Linked clips share the same `linkGroupId` and move/edit together by default.
 * Hold Alt/Option to operate on a single clip independently.
 *
 * ## Terminology
 *
 * - **index**: Raw track index in the unified scheme. Video: 0, 1, ... Audio: DEFAULT_TRACK_COUNT, DEFAULT_TRACK_COUNT+1, ...
 * - **position**: Ordinal within track type (V1=position 0, A1=position 0). Derived via `getPositionInType()`.
 * - **V↔A correspondence**: V at position N maps to A at position N.
 *
 * Both `Track.index` and `clip.trackIndex` use the same unified indexing system.
 */

import type { Clip, Track } from '@/types'
import { DEFAULT_TRACK_COUNT, MAX_TRACK_COUNT } from '@/lib/constants'
import { isKnownNoAudio } from '@/lib/audioCache'

// ═══════════════════════════════════════════════════════════════════════════════
// TRACK DATA STRUCTURES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Pre-computed track data for a sequence, used to avoid repeated filtering/sorting.
 * Create once and pass to multiple helper functions for efficiency.
 */
export interface SequenceTrackData {
  videoTracks: Track[] // Sorted by index ascending
  audioTracks: Track[] // Sorted by index ascending
  tracksByIndex: Map<number, Track>
}

/**
 * Build track data for a sequence. Call this once and reuse for multiple operations.
 */
export function buildSequenceTrackData(tracks: Track[], sequenceId: string): SequenceTrackData {
  const sequenceTracks = tracks.filter(t => t.sequenceId === sequenceId)
  return {
    videoTracks: sequenceTracks.filter(t => t.type === 'video').sort((a, b) => a.index - b.index),
    audioTracks: sequenceTracks.filter(t => t.type === 'audio').sort((a, b) => a.index - b.index),
    tracksByIndex: new Map(sequenceTracks.map(t => [t.index, t])),
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// LINKED CLIP TRACK RESOLUTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Result of resolving where a linked audio clip should be placed.
 */
export interface LinkedAudioTrackResult {
  /** The audio track to use (existing or newly created) */
  audioTrack: Track | null
  /** Whether a new audio track needs to be created */
  needsNewTrack: boolean
  /** If needsNewTrack is true, the position (0=A1, 1=A2, etc.) for the new track */
  newTrackPosition?: number
}

/**
 * Resolve the target audio track for a linked clip being placed on a video track.
 * This is the single source of truth for linked clip track correspondence.
 *
 * Rules:
 * - V1 (position 0) → A1 (position 0)
 * - V2 (position 1) → A2 (position 1)
 * - If corresponding audio track doesn't exist, signals that one should be created
 *
 * @param videoTrackIndex - The video track index where the video clip will be placed
 * @param trackData - Pre-computed track data for the sequence
 * @returns Resolution result with target audio track or creation instructions
 */
export function resolveLinkedAudioTrack(videoTrackIndex: number, trackData: SequenceTrackData): LinkedAudioTrackResult {
  const { videoTracks, audioTracks } = trackData

  // Find the position of this video track within video tracks (V1=0, V2=1, etc.)
  const videoPosition = videoTracks.findIndex(t => t.index === videoTrackIndex)
  if (videoPosition < 0) {
    // Not a valid video track
    return { audioTrack: null, needsNewTrack: false }
  }

  // Check if corresponding audio track exists
  const correspondingAudioTrack = audioTracks[videoPosition]
  if (correspondingAudioTrack) {
    return { audioTrack: correspondingAudioTrack, needsNewTrack: false }
  }

  // Corresponding audio track doesn't exist - signal creation needed
  return {
    audioTrack: null,
    needsNewTrack: true,
    newTrackPosition: videoPosition,
  }
}

/**
 * Result of calculating clip movement within track types.
 */
export interface ClipTrackMovementResult {
  /** The target track index */
  targetTrackIndex: number
  /** Whether a new track needs to be created */
  needsNewTrack: boolean
  /** If needsNewTrack is true, the position for the new track */
  newTrackPosition?: number
}

/**
 * Calculate the target track for a clip being moved, respecting track type boundaries.
 *
 * In split track mode, clips move within their track type:
 * - Video clips move among video tracks (V1, V2, V3...)
 * - Audio clips move among audio tracks (A1, A2, A3...)
 *
 * @param currentTrackIndex - Current track index of the clip
 * @param positionDelta - How many positions to move (+1 = one track "up" in UI)
 * @param trackData - Pre-computed track data for the sequence
 * @returns Movement result with target track and creation info
 */
export function calculateTrackMovement(
  currentTrackIndex: number,
  positionDelta: number,
  trackData: SequenceTrackData,
): ClipTrackMovementResult {
  const { videoTracks, audioTracks, tracksByIndex } = trackData

  const currentTrack = tracksByIndex.get(currentTrackIndex)
  if (!currentTrack) {
    // Fail loudly: if a clip exists, its track must exist in trackData
    throw new Error(
      `[calculateTrackMovement] Track index ${currentTrackIndex} not found in trackData. ` +
        `This indicates a data integrity issue - clip references a non-existent track.`,
    )
  }

  const tracksOfType = currentTrack.type === 'video' ? videoTracks : audioTracks
  const currentPosition = tracksOfType.findIndex(t => t.index === currentTrackIndex)

  if (currentPosition < 0) {
    // Fail loudly: track exists but isn't in its type's array - inconsistent trackData
    throw new Error(
      `[calculateTrackMovement] Track ${currentTrackIndex} (type: ${currentTrack.type}) not found in its type array. ` +
        `trackData is inconsistent - rebuild with buildSequenceTrackData().`,
    )
  }

  const targetPosition = currentPosition + positionDelta

  // Clamp to valid range
  if (targetPosition < 0) {
    return { targetTrackIndex: tracksOfType[0].index, needsNewTrack: false }
  }

  if (targetPosition < tracksOfType.length) {
    return { targetTrackIndex: tracksOfType[targetPosition].index, needsNewTrack: false }
  }

  // Target position is beyond existing tracks - need to create new track
  // Supported for both audio AND video tracks (drag-to-create feature)
  return {
    targetTrackIndex: -1, // Sentinel: will be set after track creation
    needsNewTrack: true,
    newTrackPosition: targetPosition,
  }
}

/**
 * Get the position of a track within its type (V1=0, V2=1, A1=0, A2=1, etc.)
 *
 * This is a query function - returns -1 if track not found, which is useful
 * for callers checking whether a target track exists. This is intentional,
 * not a silent fallback.
 *
 * @returns Position (0-based), or -1 if track index not found in trackData
 */
export function getPositionInType(trackIndex: number, trackData: SequenceTrackData): number {
  const track = trackData.tracksByIndex.get(trackIndex)
  if (!track) return -1

  const tracksOfType = track.type === 'video' ? trackData.videoTracks : trackData.audioTracks
  return tracksOfType.findIndex(t => t.index === trackIndex)
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUDIO TRACK CREATION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Result of computing the next audio track to create.
 */
export interface NextAudioTrackResult {
  /** Whether a new track can be created (false if MAX_TRACK_COUNT reached) */
  canCreate: boolean
  /** The index to use for the new audio track */
  nextIndex: number
  /** The position (A1=0, A2=1, etc.) for naming */
  nextPosition: number
}

/**
 * Compute the next available audio track index and position.
 * Use this to determine the properties for a new audio track before creating it.
 *
 * NOTE: This function checks for collisions with ALL tracks (not just audio), because
 * video tracks can "jump" to higher indices when their preferred index is taken.
 * e.g., V3 might be at index 4 if A1 took index 2. We must avoid creating A3 at index 4.
 *
 * @param trackData - Pre-computed track data for the sequence
 * @returns Result with index and position for the new track, or canCreate=false if max reached
 */
export function computeNextAudioTrack(trackData: SequenceTrackData): NextAudioTrackResult {
  const { audioTracks, tracksByIndex } = trackData

  // Compute max existing audio index, or DEFAULT_TRACK_COUNT - 1 if none exist
  const maxAudioIndex = audioTracks.length > 0 ? Math.max(...audioTracks.map(t => t.index)) : DEFAULT_TRACK_COUNT - 1

  // Find next available index that doesn't collide with ANY track (video or audio)
  let nextIndex = maxAudioIndex + 1
  while (tracksByIndex.has(nextIndex) && nextIndex < MAX_TRACK_COUNT) {
    nextIndex++
  }

  const nextPosition = audioTracks.length // 0-based position for naming (A1=0, A2=1)

  if (nextIndex >= MAX_TRACK_COUNT) {
    return { canCreate: false, nextIndex, nextPosition }
  }

  return { canCreate: true, nextIndex, nextPosition }
}

/**
 * Build a new audio track object. Caller is responsible for:
 * 1. Setting the track in yTracks
 * 2. Updating trackData for subsequent operations
 *
 * @param sequenceId - The sequence to create the track in
 * @param trackId - Unique ID for the track
 * @param trackData - Pre-computed track data for the sequence
 * @returns The new Track object, or null if MAX_TRACK_COUNT reached
 */
export function buildAudioTrack(sequenceId: string, trackId: string, trackData: SequenceTrackData): Track | null {
  const result = computeNextAudioTrack(trackData)

  if (!result.canCreate) {
    return null
  }

  return {
    id: trackId,
    sequenceId,
    index: result.nextIndex,
    name: `A${result.nextPosition + 1}`, // A1, A2, etc.
    type: 'audio',
    muted: false,
    solo: false,
    locked: false,
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// VIDEO TRACK CREATION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Result of computing the next video track to create.
 */
export interface NextVideoTrackResult {
  /** Whether a new track can be created (false if MAX_TRACK_COUNT reached) */
  canCreate: boolean
  /** The index to use for the new video track */
  nextIndex: number
  /** The position (V1=0, V2=1, etc.) for naming */
  nextPosition: number
}

/**
 * Compute the next available video track index and position.
 * Use this to determine the properties for a new video track before creating it.
 *
 * NOTE: This function checks for collisions with ALL tracks (not just video), because
 * audio tracks start at DEFAULT_TRACK_COUNT and could overlap with video indices
 * in edge cases.
 *
 * @param trackData - Pre-computed track data for the sequence
 * @returns Result with index and position for the new track, or canCreate=false if max reached
 */
export function computeNextVideoTrack(trackData: SequenceTrackData): NextVideoTrackResult {
  const { videoTracks, tracksByIndex } = trackData

  // Compute max existing video index, or -1 if none exist
  const maxVideoIndex = videoTracks.length > 0 ? Math.max(...videoTracks.map(t => t.index)) : -1

  // Find next available index that doesn't collide with ANY track (video or audio)
  let nextIndex = maxVideoIndex + 1
  while (tracksByIndex.has(nextIndex) && nextIndex < MAX_TRACK_COUNT) {
    nextIndex++
  }

  const nextPosition = videoTracks.length // 0-based position for naming (V1=0, V2=1)

  if (nextIndex >= MAX_TRACK_COUNT) {
    return { canCreate: false, nextIndex, nextPosition }
  }

  return { canCreate: true, nextIndex, nextPosition }
}

/**
 * Build a new video track object. Caller is responsible for:
 * 1. Setting the track in yTracks
 * 2. Updating trackData for subsequent operations
 *
 * @param sequenceId - The sequence to create the track in
 * @param trackId - Unique ID for the track
 * @param trackData - Pre-computed track data for the sequence
 * @returns The new Track object, or null if MAX_TRACK_COUNT reached
 */
export function buildVideoTrack(sequenceId: string, trackId: string, trackData: SequenceTrackData): Track | null {
  const result = computeNextVideoTrack(trackData)

  if (!result.canCreate) {
    return null
  }

  return {
    id: trackId,
    sequenceId,
    index: result.nextIndex,
    name: `V${result.nextPosition + 1}`, // V1, V2, etc.
    type: 'video',
    muted: false,
    solo: false,
    locked: false,
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// TRACK DATA UPDATES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Update trackData after adding a new track.
 * Returns a new SequenceTrackData with the track included.
 */
export function addTrackToData(trackData: SequenceTrackData, newTrack: Track): SequenceTrackData {
  if (newTrack.type === 'audio') {
    return {
      ...trackData,
      audioTracks: [...trackData.audioTracks, newTrack].sort((a, b) => a.index - b.index),
      tracksByIndex: new Map([...trackData.tracksByIndex, [newTrack.index, newTrack]]),
    }
  } else {
    return {
      ...trackData,
      videoTracks: [...trackData.videoTracks, newTrack].sort((a, b) => a.index - b.index),
      tracksByIndex: new Map([...trackData.tracksByIndex, [newTrack.index, newTrack]]),
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// LINK GROUP HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get all clips that share the same linkGroupId as the given clip (excluding self).
 * Returns empty array if clip has no linkGroupId.
 * Link groups are scoped to a sequence; matching by ID alone can couple duplicated
 * sequences that preserved historical linkGroupIds.
 */
export function getLinkedClips(clipId: string, clips: Map<string, Clip>): Clip[] {
  const clip = clips.get(clipId)
  if (!clip?.linkGroupId) return []

  const linked: Clip[] = []
  clips.forEach(c => {
    if (c.linkGroupId === clip.linkGroupId && c.sequenceId === clip.sequenceId && c.id !== clipId) {
      linked.push(c)
    }
  })
  return linked
}

/**
 * Get all clips in the link group including the given clip.
 * Returns array with just the clip if it has no linkGroupId.
 */
export function getAllInLinkGroup(clipId: string, clips: Map<string, Clip>): Clip[] {
  const clip = clips.get(clipId)
  if (!clip) return []
  if (!clip.linkGroupId) return [clip]

  const group: Clip[] = []
  clips.forEach(c => {
    if (c.linkGroupId === clip.linkGroupId && c.sequenceId === clip.sequenceId) {
      group.push(c)
    }
  })
  return group
}

/**
 * Check if a clip has any linked partners.
 */
export function isLinked(clipId: string, clips: Map<string, Clip>): boolean {
  return getLinkedClips(clipId, clips).length > 0
}

/**
 * Find orphaned links (link groups with only one member) and return their IDs.
 * Orphans can occur after CRDT sync conflicts or Alt+delete operations.
 *
 * Call this to get clips that need their linkGroupId cleared.
 */
export function findOrphanedLinks(clips: Map<string, Clip>): string[] {
  const linkGroups = new Map<string, string[]>() // sequenceId/linkGroupId -> clipIds

  clips.forEach(clip => {
    if (clip.linkGroupId) {
      const key = `${clip.sequenceId}/${clip.linkGroupId}`
      const group = linkGroups.get(key) || []
      group.push(clip.id)
      linkGroups.set(key, group)
    }
  })

  // Orphan = group with only 1 member
  const orphanedClipIds: string[] = []
  linkGroups.forEach(clipIds => {
    if (clipIds.length === 1) {
      orphanedClipIds.push(clipIds[0])
    }
  })

  return orphanedClipIds
}

/**
 * Expand a set of clip IDs to include all their linked clips.
 * Useful for operations that should affect entire link groups.
 */
export function expandToLinkGroups(clipIds: Set<string>, clips: Map<string, Clip>): Set<string> {
  const expanded = new Set<string>()

  clipIds.forEach(clipId => {
    const allInGroup = getAllInLinkGroup(clipId, clips)
    allInGroup.forEach(c => expanded.add(c.id))
  })

  return expanded
}

/**
 * Whether a selection can be linked into ONE new audio/video group.
 * Eligible: 2+ existing clips in one sequence, none already linked to a partner,
 * none nested-sequence or looping, all on unlocked tracks, with at least one on a
 * video track and one on an audio track, all from the same asset at the same speed,
 * covering the same timeline span and source range (a clip and its own audio).
 * Orphaned linkGroupIds count as unlinked.
 */
export function canLinkClips(
  clipIds: Iterable<string>,
  clips: ReadonlyMap<string, Clip>,
  tracks: Iterable<Track>,
): boolean {
  // Closed menus pass an empty id list on every clip change. Do not scan clips or tracks.
  const ids = new Set(clipIds)
  if (ids.size < 2) return false

  const selected: Clip[] = []
  let sequenceId = ''
  for (const id of ids) {
    const clip = clips.get(id)
    if (!clip) return false
    if (selected.length === 0) sequenceId = clip.sequenceId
    else if (clip.sequenceId !== sequenceId) return false
    if (clip.nestedSequenceId) return false
    if (clip.loop === true) return false
    selected.push(clip)
  }

  const tracksByIndex = new Map<number, Track>()
  for (const track of tracks) {
    if (track.sequenceId === sequenceId) tracksByIndex.set(track.index, track)
  }

  let sawVideo = false
  let sawAudio = false
  const selectedLinkIds = new Set<string>()
  for (const clip of selected) {
    const track = tracksByIndex.get(clip.trackIndex)
    if (!track || track.locked) return false
    if (track.type === 'video') sawVideo = true
    else if (track.type === 'audio') sawAudio = true
    if (clip.linkGroupId) selectedLinkIds.add(clip.linkGroupId)
  }
  if (!sawVideo || !sawAudio) return false
  // Linked trim and split change every member by the same amount at the same times, which holds only
  // for a clip and its own audio still in sync over the same span. Another source can run out under a
  // linked trim, and an offset partner is left behind by a split (PR #3588 review).
  const [first] = selected
  if (
    selected.some(
      clip =>
        clip.assetId !== first!.assetId ||
        (clip.speed ?? 1) !== (first!.speed ?? 1) ||
        clip.startOnTimeline !== first!.startOnTimeline ||
        clip.duration !== first!.duration ||
        clip.trimIn !== first!.trimIn ||
        clip.trimOut !== first!.trimOut,
    )
  )
    return false
  if (selectedLinkIds.size === 0) return true

  const counts = new Map<string, number>()
  for (const clip of clips.values()) {
    if (clip.sequenceId !== sequenceId || !clip.linkGroupId || !selectedLinkIds.has(clip.linkGroupId)) continue
    const next = (counts.get(clip.linkGroupId) ?? 0) + 1
    if (next >= 2) return false
    counts.set(clip.linkGroupId, next)
  }
  return true
}

// ═══════════════════════════════════════════════════════════════════════════════
// PENDING CLIP AUDIO INFO
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Audio info computed for a pending clip during drag operations.
 * Used by both local rendering and remote broadcast to show linked audio ghosts.
 */
export interface PendingClipAudioInfo {
  /** The audio track index where linked audio ghost should appear */
  linkedAudioTrackIndex?: number
  /** Whether the asset has audio (false for images) */
  hasAudio: boolean
  /** Whether a new audio track will be auto-created on drop */
  willAutoCreateAudioTrack: boolean
}

/**
 * Compute the linked audio track info for a pending clip being dropped on a video track.
 * This centralizes the logic used by both local ghost rendering and remote broadcast.
 *
 * @param assetType - The type of asset being dropped ('video', 'audio', 'image', or undefined)
 * @param targetTrackIndex - The video track index where the clip will be placed
 * @param trackData - Pre-computed track data for the sequence (null if split tracks disabled)
 * @param isCreatingNewVideoTrack - Whether a new video track is being created
 * @param videoTracks - Array of video tracks in the sequence (for reference when creating new track)
 * @param audioTracks - Array of audio tracks in the sequence
 * @returns Audio info for ghost rendering and broadcast
 */
export function computePendingClipAudioInfo(
  assetType: 'video' | 'audio' | 'image' | 'set' | 'file' | undefined,
  targetTrackIndex: number,
  trackData: SequenceTrackData | null,
  isCreatingNewVideoTrack: boolean,
  videoTracks: Track[],
  audioTracks: Track[],
  assetId?: string,
  mimeType?: string | null,
): PendingClipAudioInfo {
  // Set and image assets have no audio; also skip for videos known to have no audio track
  // Check mimeType as fallback for legacy assets without assetType
  const isAssetImage =
    assetType === 'file' ||
    assetType === 'image' ||
    assetType === 'set' ||
    (assetType == null && mimeType?.startsWith('image/') === true)
  const hasAudio = !isAssetImage && !(assetId && isKnownNoAudio(assetId))

  // Determine if this is dropping on a video track
  const targetTrack = trackData?.tracksByIndex.get(targetTrackIndex)
  const isVideoTrack = targetTrack?.type === 'video' || isCreatingNewVideoTrack

  let linkedAudioTrackIndex: number | undefined
  let willAutoCreateAudioTrack = false

  // Compute linked audio track when split tracks enabled and dropping on video track with audio
  if (trackData && isVideoTrack && hasAudio) {
    const audioResolution = resolveLinkedAudioTrack(
      isCreatingNewVideoTrack && videoTracks.length > 0
        ? videoTracks[videoTracks.length - 1].index // Use last V track as reference
        : targetTrackIndex,
      trackData,
    )

    willAutoCreateAudioTrack = audioResolution.needsNewTrack || isCreatingNewVideoTrack

    if (audioResolution.audioTrack) {
      linkedAudioTrackIndex = audioResolution.audioTrack.index
    } else if (willAutoCreateAudioTrack && audioTracks.length > 0) {
      // Show ghost at last audio track (new track will be created after it)
      linkedAudioTrackIndex = audioTracks[audioTracks.length - 1].index
    }
  }

  return {
    linkedAudioTrackIndex,
    hasAudio,
    willAutoCreateAudioTrack,
  }
}
